/* eslint-disable jsx-a11y/anchor-is-valid */
import { useMutation } from "@apollo/client";
import { Link, makeStyles, Paper, Typography } from "@material-ui/core";
import { useFlags } from "@resource/client-ffs";
import { useFlashMessage } from "components/legacy/FlashMessage/FlashMessage";
import IntegrationCard from "components/legacy/IntegrationCard";
import { SlackIntegration } from "components/legacy/Settings/Integrations/types";
import FeatureFlags from "generated/FeatureFlags";
import { DisconnectSlackIntegrationForCustomerPrisma } from "generated/schemaTypes";
import slackIconLg from "icons/slack-icon-lg.svg";
import React, { useCallback, useState } from "react";
import { ResourceCustomTheme } from "styles/config";
import { captureSentryError } from "utils/errors/sentryError";

import { DISCONNECT_SLACK_INTEGRATION_FOR_CUSTOMER } from "./PRISMA_SLACK_MUTATIONS";
import SlackIntegrationModal from "./SlackIntegrationModal";

const useMisconfiguredStyles = makeStyles((theme: ResourceCustomTheme) => ({
  root: {
    border: `1px solid ${theme.colors.ElectricRed()}`,
    backgroundColor: theme.colors.ElectricRed(100),
    color: theme.colors.ElectricRed(),
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  link: {
    textDecoration: "underline",
    color: theme.colors.ElectricRed(),
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

type MisconfiguredIntegrationProps = {
  onReconnect(): void;
};

const MisconfiguredIntegration: React.FC<MisconfiguredIntegrationProps> = ({
  onReconnect,
}) => {
  const classes = useMisconfiguredStyles();

  return (
    <Paper className={classes.root} elevation={0}>
      <Typography variant="body1" color="secondary">
        We are having trouble connecting to your Slack account!&nbsp; Please{" "}
        <Link
          className={classes.link}
          onClick={onReconnect}
          color="textSecondary"
        >
          reconnect
        </Link>
        &nbsp;to fix it.
      </Typography>
    </Paper>
  );
};

const useStyles = makeStyles((_theme: ResourceCustomTheme) => ({
  linkButton: {
    cursor: "pointer",
  },
}));

type SlackIntegrationCardProps = SlackIntegration;

const SlackIntegrationCard: React.FC<SlackIntegrationCardProps> = ({
  connected,
  needsReauthentication,
}) => {
  const classes = useStyles();
  const { [FeatureFlags.ENABLE_CHAT_ADDON]: enableChatAddon } = useFlags();
  const [open, setOpen] = useState(false);
  const { setContent } = useFlashMessage();

  const [disconnect, { loading: disconnectSaving }] =
    useMutation<DisconnectSlackIntegrationForCustomerPrisma>(
      DISCONNECT_SLACK_INTEGRATION_FOR_CUSTOMER
    );

  const onDisconnect = async () => {
    try {
      await disconnect();
    } catch (error) {
      setContent({
        content: "Something went wrong disconnecting your Slack integration",
        severity: "error",
      });
      captureSentryError(error, "SlackIntegrationCard onDisconnect");
    }
  };

  const onConnect = () => setOpen(true);
  const onClose = useCallback(() => setOpen(false), [setOpen]);

  return (
    <>
      <IntegrationCard
        icon={slackIconLg.src}
        label="Slack"
        connected={connected}
        onDisconnect={onDisconnect}
        disconnectSaving={disconnectSaving}
        onConnect={onConnect}
      >
        {connected ? (
          <>
            <Typography>
              <Link
                underline="always"
                className={classes.linkButton}
                component="span"
                onClick={() => setOpen(true)}
              >
                Update
              </Link>
              &nbsp;connection settings
            </Typography>
            {needsReauthentication && (
              <MisconfiguredIntegration onReconnect={onDisconnect} />
            )}
          </>
        ) : (
          <Typography>
            Connect Slack to automatically populate Interviewer names and
            photos.{" "}
            {enableChatAddon ? (
              <span>
                Additionally, you can receive messages from candidates who
                communicate via the in-Guide chat to a Slack channel.
              </span>
            ) : null}
          </Typography>
        )}
      </IntegrationCard>
      <SlackIntegrationModal open={open} onClose={onClose} />
    </>
  );
};

export default SlackIntegrationCard;
